// Admin moderation panel — pending / approved / rejected reviews.
// Provides one-click Approve, Reject, Delete + a WhatsApp deep-link generator
// that copies a per-parent /leave-review/<city> URL so admins can paste it
// directly into the Sunday WhatsApp parent report.

import { useEffect, useState } from "react";
import { Star, CheckCircle2, XCircle, Trash2, Loader2, RefreshCw, Clipboard, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const STATUS_FILTERS = [
    { id: "pending", label: "Pending" },
    { id: "approved", label: "Published" },
    { id: "rejected", label: "Rejected" },
    { id: "", label: "All" },
];

const CITY_FILTERS = [
    { id: "", label: "All cities" },
    { id: "jabalpur", label: "Jabalpur" },
    { id: "bhopal", label: "Bhopal" },
    { id: "nagpur", label: "Nagpur" },
    { id: "online", label: "Online" },
    { id: "other", label: "Other" },
];

export default function ReviewsModerationPanel() {
    const [status, setStatus] = useState("pending");
    const [city, setCity] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status) params.set("status", status);
            if (city) params.set("city", city);
            const { data } = await api.get(`/admin/reviews?${params.toString()}`);
            setItems(data?.reviews || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status, city]);

    const act = async (id, kind) => {
        setBusyId(id);
        try {
            if (kind === "delete") {
                await api.delete(`/admin/reviews/${id}`);
                toast.success("Deleted.");
            } else {
                await api.post(`/admin/reviews/${id}/${kind}`);
                toast.success(kind === "approve" ? "Approved & published." : "Rejected.");
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusyId(null);
        }
    };

    const copyReviewLink = (cityId) => {
        const url = `https://anandtutorials.com/leave-review${cityId ? "/" + cityId : ""}`;
        const wa = `https://wa.me/?text=${encodeURIComponent(
            `Hi! It would mean a lot if you could share a short review about your child's home-tuition experience with Anand Tutorials. Takes 60 seconds: ${url}\n\nThank you 🙏\n— Anand Tutorials Team`
        )}`;
        navigator.clipboard.writeText(wa).then(() => {
            toast.success("WhatsApp share link copied!");
        }, () => {
            toast.error("Could not copy. URL: " + wa);
        });
    };

    return (
        <div data-testid="reviews-moderation-panel" className="space-y-5">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold text-[#0F172A] flex items-center gap-2">
                        <Star className="w-6 h-6 text-amber-500" /> Parent Reviews
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Moderate parent reviews. Approved reviews auto-inject into the LocalBusiness JSON-LD on city pages.
                    </p>
                </div>
                <button onClick={load} className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5" data-testid="reviews-refresh">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
                </button>
            </header>

            {/* WhatsApp deep-link generator */}
            <section className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                    <MessageCircle className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <div className="font-semibold text-[#0F172A]">WhatsApp review request — one-click copy</div>
                        <p className="text-sm text-slate-600 mt-1">
                            Generates a ready-to-share WhatsApp message containing a per-city review link. Paste into your Sunday parent report.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {CITY_FILTERS.filter(c => c.id).map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => copyReviewLink(c.id)}
                                    data-testid={`wa-link-${c.id}`}
                                    className="px-3 py-1.5 text-sm rounded-lg bg-white border border-emerald-200 hover:bg-emerald-100 inline-flex items-center gap-1.5"
                                >
                                    <Clipboard className="w-3.5 h-3.5" /> {c.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                    {STATUS_FILTERS.map((s) => (
                        <button
                            key={s.id || "all"}
                            onClick={() => setStatus(s.id)}
                            data-testid={`review-status-filter-${s.id || "all"}`}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${status === s.id ? "bg-white text-[#0F172A] shadow-sm" : "text-slate-500 hover:text-[#0F172A]"}`}
                        >{s.label}</button>
                    ))}
                </div>
                <select
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    data-testid="review-city-filter"
                    className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white"
                >
                    {CITY_FILTERS.map((c) => <option key={c.id || "all"} value={c.id}>{c.label}</option>)}
                </select>
            </div>

            {/* List */}
            {loading ? (
                <div className="text-sm text-slate-500 py-8 text-center">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500">
                    No reviews in this view.
                </div>
            ) : (
                <ul className="space-y-3">
                    {items.map((r) => (
                        <li key={r.id} data-testid={`review-row-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-5">
                            <div className="flex items-start justify-between gap-3 mb-2">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-[#0F172A]">{r.parent_name}</span>
                                        <span className="text-xs text-slate-500">·</span>
                                        <span className="text-xs uppercase tracking-wider text-slate-500">{r.city}</span>
                                        {r.locality && <span className="text-xs text-slate-400">/ {r.locality}</span>}
                                    </div>
                                    {(r.student_grade || r.subject_or_board) && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                            {[r.student_grade, r.subject_or_board].filter(Boolean).join(" · ")}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                                    ))}
                                </div>
                            </div>
                            {r.review_title && <div className="font-semibold text-[#0F172A] mt-1">{r.review_title}</div>}
                            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.review_body}</p>
                            <div className="text-xs text-slate-400 mt-3">{new Date(r.created_at).toLocaleString()}</div>
                            <div className="flex items-center gap-2 mt-3">
                                {r.status !== "approved" && (
                                    <button
                                        onClick={() => act(r.id, "approve")}
                                        disabled={busyId === r.id}
                                        data-testid={`review-approve-${r.id}`}
                                        className="px-3 py-1.5 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 inline-flex items-center gap-1"
                                    >
                                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve & Publish
                                    </button>
                                )}
                                {r.status !== "rejected" && (
                                    <button
                                        onClick={() => act(r.id, "reject")}
                                        disabled={busyId === r.id}
                                        data-testid={`review-reject-${r.id}`}
                                        className="px-3 py-1.5 text-xs rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60 inline-flex items-center gap-1"
                                    >
                                        <XCircle className="w-3.5 h-3.5" /> Reject
                                    </button>
                                )}
                                <button
                                    onClick={() => { if (window.confirm("Delete this review permanently?")) act(r.id, "delete"); }}
                                    disabled={busyId === r.id}
                                    data-testid={`review-delete-${r.id}`}
                                    className="px-3 py-1.5 text-xs rounded-md bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-60 inline-flex items-center gap-1 ml-auto"
                                >
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                            {r.status === "approved" && (
                                <div className="text-xs text-emerald-700 mt-2 flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Published — auto-injected into LocalBusiness schema for {r.city}.
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
